import { useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Calendar, Loader2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useRequiredFields } from "@/hooks/useRequiredFields";
import { describeApiError, type FriendlyError } from "@/lib/apiErrorMessages";
import { FieldError, MissingFieldsSummary, SubmitErrorNotice } from "@/components/common/FormFeedback";
import { AccountInlinePicker } from "@/components/account/AccountInlinePicker";
import { useCreateContract, useUploadMainDocument, useContractPriceTypes } from "@/hooks/useContracts";
import { fetchAvailableLegalEntities, updateContractTerms } from "@/api/contracts";
import { uploadAttachment } from "@/api/attachments";
import { ContractTermsFields, type ContractTermsValue } from "./ContractTermsFields";
import { CONTRACT_TYPE_LABEL } from "./constants";
import type { ContractCreatePayload, ContractType } from "@/api/types";

type EntryPoint = "account" | "list";

interface PrefilledAccount {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  entryPoint: EntryPoint;
  account?: PrefilledAccount | null;
  /**
   * Contract UNIFICATION — override where we land after create (default:
   * standalone /contracts/:id). Client360 passes the holding-context detail
   * route so the new draft opens in the SAME detail without leaving the client.
   */
  onCreated?: (contractId: string) => void;
}

interface LegalEntityOption {
  id: string;
  name: string;
  label?: string;
  defaultForAll?: boolean;
}

export function CreateContractDrawer({ open, onClose, entryPoint, account, onCreated }: Props) {
  const navigate = useNavigate();
  const panelRef = useDrawerA11y(open, () => onClose());
  const headingId = useId();
  const numberId = useId();
  const effId = useId();
  const expId = useId();

  const [step, setStep] = useState<"meta" | "terms" | "document">("meta");
  const [contractType, setContractType] = useState<ContractType>("supply_agreement");
  const [otherSpec, setOtherSpec] = useState("");
  const [legalEntityId, setLegalEntityId] = useState("");
  const [legalEntities, setLegalEntities] = useState<LegalEntityOption[]>([]);
  const [legalLoading, setLegalLoading] = useState(false);
  // G1-S1 — commercial-terms step state (live 8-set commercial half; settlement excluded per D-B).
  const [terms, setTerms] = useState<ContractTermsValue>({
    deferralDays: "",
    prepayment: "",
    creditLimit: "",
    currency: "UAH",
    priceTypeId: "",
  });
  const [termsSaving, setTermsSaving] = useState(false);
  const priceTypesQ = useContractPriceTypes();
  const [effectiveDate, setEffectiveDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [contractNumber, setContractNumber] = useState("");
  const [submitError, setSubmitError] = useState<FriendlyError | null>(null);

  const [pickedAccount, setPickedAccount] = useState<PrefilledAccount | null>(null);
  const [accountSearch, setAccountSearch] = useState("");
  const [accountPickerOpen, setAccountPickerOpen] = useState(false);
  // BUG2 — a context prefill (entryPoint="list" + account) stays CHANGEABLE:
  // «Змінити» clears the prefill so the picker takes over.
  const [accountCleared, setAccountCleared] = useState(false);

  const [createdId, setCreatedId] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const accountLocked = entryPoint === "account" && !!account;
  const effectiveAccount = accountCleared ? pickedAccount : (account ?? pickedAccount);

  const createMut = useCreateContract();
  const uploadMainMut = useUploadMainDocument(createdId);

  // §0 — «Далі» never disabled for gaps: press → list + jump to the gap.
  const req = useRequiredFields([
    {
      key: "account",
      label: "Клієнт",
      missing: !effectiveAccount,
      message: accountSearch.trim() ? "Оберіть клієнта зі списку" : "Оберіть клієнта",
    },
    {
      key: "legalEntity",
      label: "Юр. особа Kormax",
      missing: !legalEntityId,
      message:
        !legalLoading && legalEntities.length === 0
          ? "Немає доступної юр. особи — зверніться до адміністратора"
          : "Оберіть юр. особу Kormax",
    },
  ]);
  const resetRequired = req.reset;

  useEffect(() => {
    if (!open) {
      setStep("meta");
      setContractType("supply_agreement");
      setOtherSpec("");
      setLegalEntityId("");
      setEffectiveDate("");
      setExpirationDate("");
      setContractNumber("");
      setSubmitError(null);
      resetRequired();
      setPickedAccount(null);
      setAccountSearch("");
      setAccountPickerOpen(false);
      setAccountCleared(false);
      setCreatedId(null);
      setFile(null);
      setTerms({ deferralDays: "", prepayment: "", creditLimit: "", currency: "UAH", priceTypeId: "" });
      setTermsSaving(false);
    }
  }, [open, resetRequired]);

  // G2-S2 — load the LEs SELECTABLE for a new contract (D2=C, server flag-gated).
  // Pre-selects the defaultForAll LE. Non-selectable LEs are hidden by the server.
  useEffect(() => {
    if (!open || legalEntities.length > 0 || legalLoading) return;
    setLegalLoading(true);
    fetchAvailableLegalEntities()
      .then((res) => {
        const list = res.list ?? [];
        setLegalEntities(list);
        if (res.defaultId && !legalEntityId) {
          setLegalEntityId(res.defaultId);
        }
      })
      .catch(() => setLegalEntities([]))
      .finally(() => setLegalLoading(false));
  }, [open, legalEntities.length, legalLoading, legalEntityId]);

  if (!open) return null;

  const submitMeta = async () => {
    setSubmitError(null);
    if (!req.validate() || !effectiveAccount) return;
    const payload: ContractCreatePayload = {
      accountId: effectiveAccount.id,
      legalEntityId,
      contractType,
      contractTypeOtherSpecification:
        contractType === "other" && otherSpec.trim() ? otherSpec.trim() : undefined,
      effectiveDate: effectiveDate || undefined,
      expirationDate: expirationDate || undefined,
      contractNumber: contractNumber.trim() || undefined,
    };
    try {
      const created = await createMut.mutateAsync(payload);
      setCreatedId(created.id);
      setStep("terms");
    } catch (err) {
      setSubmitError(describeApiError(err, "Не вдалося створити договір"));
    }
  };

  // G1-S1 — persist commercial terms on the just-created draft, then to document.
  const num = (s: string): number | null => (s.trim() === "" ? null : Number(s));
  const saveTermsAndNext = async () => {
    if (!createdId) return;
    setTermsSaving(true);
    setSubmitError(null);
    try {
      await updateContractTerms(createdId, {
        deferralDays: num(terms.deferralDays),
        prepaymentPercent: num(terms.prepayment),
        creditLimit: num(terms.creditLimit),
        currency: terms.currency.trim() || null,
        priceTypeId: terms.priceTypeId || null,
        legalEntityId,
      });
      setStep("document");
    } catch (e) {
      setSubmitError(describeApiError(e, "Не вдалося зберегти умови"));
    } finally {
      setTermsSaving(false);
    }
  };

  const goToDetail = (cid: string) => {
    if (onCreated) onCreated(cid);
    else navigate(`/contracts/${cid}`);
  };

  const skipDocument = () => {
    if (createdId) goToDetail(createdId);
    onClose();
  };

  const submitDocument = async () => {
    if (!createdId || !file) return;
    setUploading(true);
    setSubmitError(null);
    try {
      const att = await uploadAttachment(file, "CContractAttachment", "file");
      await uploadMainMut.mutateAsync({ fileId: att.id });
      goToDetail(createdId);
      onClose();
    } catch (e) {
      setSubmitError(describeApiError(e, "Не вдалося завантажити документ"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full sm:w-[480px] overflow-y-auto bg-background shadow-2xl outline-none"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="text-base font-semibold">
            Новий договір
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити форму"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="border-b bg-muted/30 px-5 py-2 flex items-center gap-2 text-xs text-muted-foreground">
          <span className={cn("inline-flex items-center gap-1", step === "meta" && "text-primary font-medium")}>
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/10 text-primary text-[12px]">1</span>
            Метадані
          </span>
          <span className="text-muted-foreground/40">›</span>
          <span className={cn("inline-flex items-center gap-1", step === "terms" && "text-primary font-medium")}>
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-muted text-[12px]">2</span>
            Комерційні умови
          </span>
          <span className="text-muted-foreground/40">›</span>
          <span className={cn("inline-flex items-center gap-1", step === "document" && "text-primary font-medium")}>
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-muted text-[12px]">3</span>
            Документ
          </span>
        </div>

        {step === "meta" && (
          <>
            <div className="space-y-4 px-5 py-5">
              <div ref={req.fieldRef("account")}>
                <label className="mb-1.5 block text-xs font-medium">
                  Клієнт <span className="text-destructive">*</span>
                </label>
                {effectiveAccount ? (
                  <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
                    <span className="flex items-center gap-2 truncate">
                      <Building2 className="w-4 h-4 text-muted-foreground" aria-hidden />
                      {effectiveAccount.name}
                    </span>
                    {!accountLocked && (
                      <button
                        type="button"
                        onClick={() => {
                          setPickedAccount(null);
                          setAccountCleared(true);
                          setAccountPickerOpen(true);
                        }}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        Змінити
                      </button>
                    )}
                  </div>
                ) : (
                  <AccountInlinePicker
                    query={accountSearch}
                    onQueryChange={setAccountSearch}
                    isOpen={accountPickerOpen || accountSearch.length >= 2}
                    onOpen={() => setAccountPickerOpen(true)}
                    onPick={(a) => {
                      setPickedAccount(a);
                      setAccountSearch("");
                      setAccountPickerOpen(false);
                    }}
                    error={req.errorFor("account")}
                    errorId={req.errorId("account")}
                  />
                )}
              </div>

              <div ref={req.fieldRef("legalEntity")}>
                <label className="mb-1.5 block text-xs font-medium">
                  Юр. особа Kormax <span className="text-destructive">*</span>
                </label>
                {!legalLoading && legalEntities.length === 0 ? (
                  <p
                    tabIndex={-1}
                    className="rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-xs text-warning-foreground"
                  >
                    Вам не призначено жодної юр.особи для створення договорів. Зверніться до адміністратора.
                  </p>
                ) : (
                  <select
                    value={legalEntityId}
                    onChange={(e) => setLegalEntityId(e.target.value)}
                    disabled={legalLoading}
                    {...req.invalidProps("legalEntity")}
                    className={cn(
                      "flex h-9 w-full rounded-md border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      req.errorFor("legalEntity") && "border-destructive",
                    )}
                  >
                    <option value="">{legalLoading ? "Завантаження…" : "Оберіть юр. особу"}</option>
                    {legalEntities.map((le) => (
                      <option key={le.id} value={le.id}>
                        {le.label ? `${le.name} — ${le.label}` : le.name}
                      </option>
                    ))}
                  </select>
                )}
                <FieldError id={req.errorId("legalEntity")} message={req.errorFor("legalEntity")} />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium">Тип договору</label>
                <select
                  value={contractType}
                  onChange={(e) => setContractType(e.target.value as ContractType)}
                  className="flex h-9 w-full rounded-md border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {(Object.keys(CONTRACT_TYPE_LABEL) as ContractType[]).map((t) => (
                    <option key={t} value={t}>{CONTRACT_TYPE_LABEL[t]}</option>
                  ))}
                </select>
                {contractType === "other" && (
                  <Input
                    className="mt-2"
                    placeholder="Уточніть тип"
                    value={otherSpec}
                    onChange={(e) => setOtherSpec(e.target.value)}
                  />
                )}
              </div>

              <div>
                <label htmlFor={numberId} className="mb-1.5 block text-xs font-medium">
                  Номер договору <span className="text-muted-foreground">(опц.)</span>
                </label>
                <Input
                  id={numberId}
                  value={contractNumber}
                  onChange={(e) => setContractNumber(e.target.value)}
                  placeholder="Авто-номер призначиться, якщо порожньо"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor={effId} className="mb-1.5 block text-xs font-medium">
                    Початок дії
                  </label>
                  <div className="flex items-center gap-2 rounded-md border px-2.5 py-1.5">
                    <Calendar className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
                    <input
                      id={effId}
                      type="date"
                      value={effectiveDate}
                      onChange={(e) => setEffectiveDate(e.target.value)}
                      className="w-full bg-transparent text-sm outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor={expId} className="mb-1.5 block text-xs font-medium">
                    Кінець дії
                  </label>
                  <div className="flex items-center gap-2 rounded-md border px-2.5 py-1.5">
                    <Calendar className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
                    <input
                      id={expId}
                      type="date"
                      value={expirationDate}
                      onChange={(e) => setExpirationDate(e.target.value)}
                      className="w-full bg-transparent text-sm outline-none"
                    />
                  </div>
                </div>
              </div>

            </div>

            <div className="sticky bottom-0 border-t bg-background px-5 py-4">
              <MissingFieldsSummary missing={req.missing} onJump={req.focusField} />
              <SubmitErrorNotice error={submitError} />
              <Button
                type="button"
                onClick={submitMeta}
                disabled={createMut.isPending}
                className="w-full h-11 text-sm font-semibold"
              >
                {createMut.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Створення…
                  </>
                ) : (
                  "Далі — Комерційні умови"
                )}
              </Button>
              <p className="mt-1.5 text-center text-xs text-muted-foreground">
                Створює чернетку. Подача на погодження — на сторінці деталей.
              </p>
            </div>
          </>
        )}

        {step === "terms" && createdId && (
          <>
            <div className="space-y-4 px-5 py-5">
              <p className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                Крок 2/3. Editable у чернетці; після подання на погодження умови блокуються (snapshot).
                Тип цін обов'язковий перед поданням — можна обрати зараз або на сторінці деталей.
              </p>
              <ContractTermsFields
                value={terms}
                onChange={setTerms}
                priceTypes={priceTypesQ.data?.items ?? []}
                priceTypesLoading={priceTypesQ.isPending}
              />
            </div>
            <div className="sticky bottom-0 border-t bg-background px-5 py-4">
              <SubmitErrorNotice error={submitError} />
              <Button
                type="button"
                onClick={saveTermsAndNext}
                disabled={termsSaving}
                className="w-full h-11 text-sm font-semibold gap-1.5"
              >
                {termsSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Збереження…
                  </>
                ) : (
                  "Далі — Документ"
                )}
              </Button>
            </div>
          </>
        )}

        {step === "document" && createdId && (
          <>
            <div className="space-y-4 px-5 py-5">
              <p className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                Чернетка створена. Завантажте файл договору (DOCX/PDF), або пропустіть — додасте пізніше.
              </p>
              <div>
                <label className="mb-1.5 block text-xs font-medium">
                  Файл договору
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className={cn(
                    "block w-full text-sm",
                    "file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary-foreground hover:file:bg-primary/90",
                  )}
                />
              </div>
            </div>
            <div className="sticky bottom-0 border-t bg-background px-5 py-4">
              <SubmitErrorNotice error={submitError} />
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={skipDocument} className="flex-1 h-11">
                  Пропустити
                </Button>
                <Button
                  type="button"
                  onClick={submitDocument}
                  disabled={!file || uploading || uploadMainMut.isPending}
                  className="flex-1 h-11 gap-1.5"
                >
                  {uploading || uploadMainMut.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Завантаження…
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" /> Завантажити
                    </>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
